import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  getDocFromCache,
  runTransaction,
  setDoc,
  writeBatch,
  type DocumentSnapshot,
} from 'firebase/firestore';
import { userCollectionBagDoc, userDoc } from '../firebase/collections';
import { db } from '../config/firebase';
import { useAuth } from './useAuth';
import { useCollectionSummary } from './useCollectionSummary';
import type { CollectionEntry } from '../types';
import { AnalyticsEvents, trackEvent } from '../analytics/events';
import { clearAllCustomBinderPlacements } from '../utils/binderPlacementSync';
import {
  applyCollectionEntryDelta,
  COLLECTION_SUMMARY_SET_OPTIONS,
  collectionSummaryToFirestore,
  emptyCollectionSummary,
  parseCollectionSummary,
  summarizeCollection,
  summaryNeedsHeal,
  type CollectionSummary,
} from '../utils/collectionSummary';
import {
  assertCollectionBagSize,
  collectionBagToFirestore,
  parseCollectionBag,
  setCollectionBagEntry,
  writeCollectionBag,
} from '../utils/collectionBag';
import {
  canTrustEmptyBagSnapshot,
  applyCollectionBagEntryUpdate,
  applyCollectionBagFieldUpdate,
  resolveMutationBaseSummary,
} from '../utils/collectionMutation';
import {
  CollectionOutbox,
  OUTBOX_FLUSH_DELAY_MS,
  planOutboxUpdates,
} from '../utils/collectionOutbox';
import { riftboundToaster } from '../components/ui/toaster';
import { countedGetDoc, countedOnDocSnapshot } from '../data/firestoreReads';
import { isFirestoreQuotaExceeded, wrapFirestoreQuotaError } from '../utils/firestoreQuota';
import { prefetchCollectionCatalogStats } from './useCollectionCatalogStats';
import { fetchCardPrintsCached, peekCardPrints } from '../utils/cardPrintCache';
import type { SetLanguage } from '../constants';
import { applyPrintedLanguageDelta } from '../utils/collectionLots';

export type CollectionEntryPatch = Partial<
  Pick<CollectionEntry, 'quantity' | 'quantityFoil' | 'notes' | 'lots'>
> & {
  quantityDelta?: number;
  quantityFoilDelta?: number;
  /**
   * Printed language of the copies this delta adds or removes. English stays
   * an unallocated copy. `zh`, `ko`, and `fr` land on a lot.
   */
  copyLanguage?: SetLanguage;
};

export type CollectionMutationResult = {
  quantity: number;
  quantityFoil: number;
  lots?: CollectionEntry['lots'];
};

export type CollectionMutationOptions = {
  /**
   * Write before resolving instead of joining the coalescing window. Use where
   * losing an unflushed patch is unacceptable — the scanner commits one card at
   * a time and the user moves on immediately.
   */
  immediate?: boolean;
};

interface ContextValue {
  entries: Record<string, CollectionEntry>;
  loading: boolean;
  /** True once a page has asked for the full-collection listener this session. */
  entriesLive: boolean;
  upsertEntry: (
    cardId: string,
    patch: CollectionEntryPatch,
    options?: CollectionMutationOptions,
  ) => Promise<CollectionMutationResult | undefined>;
  /** Write anything the coalescing window is still holding. */
  flushEntries: () => Promise<void>;
  removeEntry: (cardId: string) => Promise<void>;
  clearCollection: () => Promise<void>;
  getEntry: (cardId: string) => CollectionEntry | undefined;
  totalCards: number;
  uniqueCards: number;
  totalFoilCards: number;
  retainEntries: () => void;
}

export type UseCollectionOptions = {
  /**
   * When false, read whatever is already in context without attaching the
   * full-collection listener. Default true — pages that need entries or
   * mutations should keep the default.
   */
  subscribe?: boolean;
};

const CollectionContext = createContext<ContextValue | null>(null);

function resolveQuantity(
  existingQty: number,
  patch: CollectionEntryPatch,
): number {
  if (patch.quantityDelta != null) {
    return Math.max(0, existingQty + patch.quantityDelta);
  }
  if (patch.quantity != null) {
    return Math.max(0, patch.quantity);
  }
  return existingQty > 0 ? existingQty : 1;
}

function resolveQuantityFoil(
  existingFoil: number,
  nextQuantity: number,
  patch: CollectionEntryPatch,
): number {
  if (patch.quantityFoilDelta != null) {
    return Math.min(Math.max(0, existingFoil + patch.quantityFoilDelta), nextQuantity);
  }
  if (patch.quantityFoil != null) {
    return Math.min(Math.max(0, patch.quantityFoil), nextQuantity);
  }
  return Math.min(Math.max(0, existingFoil), nextQuantity);
}

type EntryMutationPlan = {
  nextEntries: Record<string, CollectionEntry>;
  nextSummary: CollectionSummary;
  nextQuantity: number;
  nextQuantityFoil: number;
  previousQuantity: number;
  previousQuantityFoil: number;
};

function planEntryMutation(
  current: Record<string, CollectionEntry>,
  baseSummary: CollectionSummary,
  cardId: string,
  patch: CollectionEntryPatch,
): EntryMutationPlan {
  const existing = current[cardId];
  const existingQty = existing?.quantity ?? 0;
  const existingFoil = existing?.quantityFoil ?? 0;
  const nextQty = resolveQuantity(existingQty, patch);
  const nextFoil = resolveQuantityFoil(existingFoil, nextQty, patch);
  const tracksLanguage = Boolean(patch.copyLanguage && patch.copyLanguage !== 'en');
  const languageLots = tracksLanguage
    ? applyPrintedLanguageDelta(
      existing?.lots,
      patch.copyLanguage,
      patch.quantityDelta ?? 0,
      patch.quantityFoilDelta ?? 0,
    )
    : undefined;
  const nextEntries = setCollectionBagEntry(current, cardId, {
    quantity: nextQty,
    quantityFoil: nextFoil,
    notes: patch.notes ?? existing?.notes,
    lots: tracksLanguage
      ? (languageLots ?? [])
      : (patch.lots !== undefined ? patch.lots : existing?.lots),
  });
  const nextSummary = applyCollectionEntryDelta(
    baseSummary,
    cardId,
    existingQty,
    existingFoil,
    nextQty,
    nextFoil,
  );
  return {
    nextEntries,
    nextSummary,
    nextQuantity: nextQty,
    nextQuantityFoil: nextFoil,
    previousQuantity: existingQty,
    previousQuantityFoil: existingFoil,
  };
}

async function readOrBootstrapSummary(
  uid: string,
  hint: CollectionSummary | null | undefined,
): Promise<CollectionSummary | null> {
  if (hint) return hint;
  const snap = await countedGetDoc(userDoc(uid));
  const parsed = parseCollectionSummary(snap.data()?.collectionSummary);
  if (parsed) return parsed;
  const { loadUserCollectionMap } = await import(
    '../features/teams/utils/teamSharedCollectionSync'
  );
  const map = await loadUserCollectionMap(uid);
  return summarizeCollection(map, 1);
}

/** Serialize bag mutations so scan/FAB bursts do not contend on one document. */
function createMutationQueue() {
  let chain: Promise<unknown> = Promise.resolve();
  return function enqueue<T>(task: () => Promise<T>): Promise<T> {
    const run = chain.then(task, task);
    chain = run.then(
      () => undefined,
      () => undefined,
    );
    return run;
  };
}


export function CollectionProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  /** Server-confirmed summary from the auth user-doc listener. */
  const liveSummary = useCollectionSummary();
  const uid = user?.uid ?? null;
  const [entries, setEntries] = useState<Record<string, CollectionEntry>>({});
  const [loading, setLoading] = useState(false);
  const [wantEntries, setWantEntries] = useState(false);
  const [entriesReady, setEntriesReady] = useState(false);

  const retainEntries = useCallback(() => {
    setWantEntries(true);
  }, []);
  const summaryBootstrapUid = useRef<string | null>(null);
  const bootstrapInFlight = useRef<Promise<CollectionSummary | null> | null>(null);
  const entriesRef = useRef(entries);
  const wantEntriesRef = useRef(wantEntries);
  /** True once we have a bag snapshot or IndexedDB cache hit (safe to mutate). */
  const bagHydratedRef = useRef(false);
  /** Whether the hydrated bag document currently exists (vs confirmed missing). */
  const bagExistsRef = useRef(false);
  /** True when the last applied bag snapshot came from the local cache. */
  const bagFromCacheRef = useRef(true);
  const summaryRef = useRef<CollectionSummary | null | undefined>(liveSummary);
  const enqueueMutation = useRef(createMutationQueue()).current;
  const outbox = useRef(new CollectionOutbox()).current;
  const flushTimer = useRef<number | null>(null);
  entriesRef.current = entries;
  wantEntriesRef.current = wantEntries;
  if (liveSummary) {
    // Prefer the auth listener's summary when it is at least as new as our
    // last local write (higher or equal revision).
    const local = summaryRef.current;
    if (!local || liveSummary.revision >= local.revision) {
      summaryRef.current = liveSummary;
    }
  }

  useEffect(() => {
    if (!uid) {
      setWantEntries(false);
      setEntries({});
      setLoading(false);
      setEntriesReady(false);
      summaryBootstrapUid.current = null;
      bootstrapInFlight.current = null;
      bagHydratedRef.current = false;
      bagExistsRef.current = false;
      bagFromCacheRef.current = true;
      summaryRef.current = null;
      // Signing out leaves no bag to write these against, and the ids belong
      // to the previous account.
      outbox.clear();
    }
  }, [uid, outbox]);

  useEffect(() => {
    if (liveSummary) bootstrapInFlight.current = null;
  }, [liveSummary]);

  const ensureSummary = useCallback(async () => {
    if (!uid) return null;
    if (liveSummary) return liveSummary;
    if (!bootstrapInFlight.current) {
      bootstrapInFlight.current = readOrBootstrapSummary(uid, null);
    }
    return bootstrapInFlight.current;
  }, [uid, liveSummary]);

  useEffect(() => {
    if (!uid || !wantEntries) {
      return;
    }
    // Warm set-completion denominators while the user is elsewhere so /collection
    // does not wait on /api/catalog-rarity-stats on first paint.
    prefetchCollectionCatalogStats();
    setLoading(true);
    let cancelled = false;
    const bagRef = userCollectionBagDoc(uid);

    const applyBagSnap = (snap: DocumentSnapshot) => {
      if (cancelled) return;
      bagFromCacheRef.current = snap.metadata.fromCache;
      if (snap.exists()) {
        setEntries(parseCollectionBag(snap.data()) ?? {});
        bagExistsRef.current = true;
        bagHydratedRef.current = true;
        setEntriesReady(true);
        setLoading(false);
        return;
      }

      // Missing bag — only treat as empty for writes when the miss is
      // server-confirmed or the summary already says zero uniques.
      setEntries({});
      bagExistsRef.current = false;
      const summaryUnique = summaryRef.current?.uniqueCards ?? null;
      bagHydratedRef.current = canTrustEmptyBagSnapshot({
        fromCache: snap.metadata.fromCache,
        summaryUniqueCards: summaryUnique,
      });
      setEntriesReady(true);
      setLoading(false);
    };

    const unsub = countedOnDocSnapshot(
      bagRef,
      (snap) => applyBagSnap(snap),
      (err) => {
        console.error('[useCollection] snapshot error', err);
        if (cancelled) return;
        // Read-cap exhaustion still lets IndexedDB serve an existing bag.
        void getDocFromCache(bagRef)
          .then((snap) => {
            if (cancelled) return;
            if (snap.exists()) {
              applyBagSnap(snap);
              return;
            }
            // Cached miss is not proof the bag is empty — do not hydrate.
            setLoading(false);
            setEntriesReady(true);
            bagHydratedRef.current = false;
            bagExistsRef.current = false;
            bagFromCacheRef.current = true;
          })
          .catch((cacheErr) => {
            console.warn('[useCollection] bag cache read failed', cacheErr);
            if (!cancelled) {
              setLoading(false);
              if (isFirestoreQuotaExceeded(err)) {
                setEntriesReady(true);
                bagHydratedRef.current = false;
              }
            }
          });
      },
    );
    return () => {
      cancelled = true;
      unsub();
    };
  }, [uid, wantEntries]);

  // Prefetch owned prints as soon as Firestore ids arrive so opening the
  // Collection tab paints from cache instead of starting the batch fetch cold.
  const ownedIdsKey = useMemo(
    () => Object.keys(entries).sort().join(','),
    [entries],
  );
  useEffect(() => {
    if (!uid || !ownedIdsKey) return;
    const ids = ownedIdsKey.split(',');
    const { missingIds } = peekCardPrints(ids);
    if (missingIds.length === 0) return;
    void fetchCardPrintsCached(ids).catch(() => {
      // Non-fatal — CollectionPage will retry on mount.
    });
  }, [uid, ownedIdsKey]);

  // Bootstrap or repair `collectionSummary` from the live bag, at most once per
  // session. Recompute-and-compare rather than spot-checking individual fields:
  // the 30 Aug migration wrote correct totals with an empty `bySet`, and a
  // single incremental delta then made any emptiness check unable to fire.
  useEffect(() => {
    if (!uid || !wantEntries || !entriesReady) return;
    if (summaryBootstrapUid.current === uid) return;
    // Never derive a summary from an untrusted bag: a cached snapshot or a
    // quota fallback would persist a wrong summary at a winning revision.
    if (!bagHydratedRef.current || bagFromCacheRef.current) return;

    const existing = liveSummary;
    const canonical = summarizeCollection(entries, 1);
    if (!summaryNeedsHeal(existing, canonical)) return;

    summaryBootstrapUid.current = uid;
    // The revision bump is load-bearing: the auth listener only accepts a
    // server summary at >= the local revision, and `isTeamProjectionCurrent`
    // compares against it to decide whether to re-project for the team.
    const summary: CollectionSummary = {
      ...canonical,
      revision: existing ? existing.revision + 1 : 1,
    };
    void setDoc(
      userDoc(uid),
      { collectionSummary: collectionSummaryToFirestore(summary) },
      COLLECTION_SUMMARY_SET_OPTIONS,
    ).then(() => {
      summaryRef.current = summary;
    }).catch((err) => {
      console.warn('[useCollection] collectionSummary heal failed', err);
      if (summaryBootstrapUid.current === uid) summaryBootstrapUid.current = null;
    });
  }, [uid, wantEntries, entriesReady, entries, liveSummary]);

  const cancelScheduledFlush = useCallback(() => {
    if (flushTimer.current === null) return;
    window.clearTimeout(flushTimer.current);
    flushTimer.current = null;
  }, []);

  /**
   * Write every pending card. Must be called from inside the mutation queue —
   * it deliberately does not enqueue itself, so callers already holding the
   * queue (`upsertEntry`) can flush inline without deadlocking.
   */
  const writePendingEntries = useCallback(async () => {
    if (!uid || outbox.size === 0) return;
    cancelScheduledFlush();
    const dirty = outbox.drain();
    const snapshot = entriesRef.current;
    const summary = summaryRef.current;
    if (!summary) {
      outbox.requeue(dirty);
      return;
    }
    try {
      const bagRef = userCollectionBagDoc(uid);
      const profileRef = userDoc(uid);
      const summaryWrite = { collectionSummary: collectionSummaryToFirestore(summary) };

      if (!bagExistsRef.current) {
        const payload = collectionBagToFirestore(snapshot, summary.revision);
        assertCollectionBagSize(payload);
        const batch = writeBatch(db);
        batch.set(bagRef, payload);
        batch.set(profileRef, summaryWrite, COLLECTION_SUMMARY_SET_OPTIONS);
        await batch.commit();
        bagExistsRef.current = true;
        return;
      }

      // Incremental writes never checked the size guard, so a bag could grow
      // past it and only fail much later on some unrelated full rewrite.
      assertCollectionBagSize(collectionBagToFirestore(snapshot, summary.revision));

      const chunks = planOutboxUpdates(snapshot, dirty, summary.revision);
      for (let i = 0; i < chunks.length; i += 1) {
        const batch = writeBatch(db);
        applyCollectionBagFieldUpdate(batch, bagRef, chunks[i]);
        // The summary is the totals for the whole bag, so it is only correct
        // once every chunk has landed.
        if (i === chunks.length - 1) {
          batch.set(profileRef, summaryWrite, COLLECTION_SUMMARY_SET_OPTIONS);
        }
        await batch.commit();
      }
    } catch (err) {
      // Newer edits to the same cards are already in the set; re-adding the
      // drained ids cannot undo them.
      outbox.requeue(dirty);
      throw wrapFirestoreQuotaError(err);
    }
  }, [uid, outbox, cancelScheduledFlush]);

  const flushEntries = useCallback(
    () => enqueueMutation(writePendingEntries),
    [enqueueMutation, writePendingEntries],
  );

  /**
   * Guarantee a write within one delay of the *first* pending patch rather
   * than resetting on each one, so holding the quantity spinner still saves.
   */
  const scheduleFlush = useCallback(() => {
    if (flushTimer.current !== null) return;
    flushTimer.current = window.setTimeout(() => {
      flushTimer.current = null;
      void flushEntries().catch((err) => {
        console.error('[useCollection] deferred collection write failed', err);
        riftboundToaster.create({
          title: isFirestoreQuotaExceeded(err) ? 'Quota exceeded' : 'Could not save',
          description:
            err instanceof Error
              ? err.message
              : 'Some collection changes were not saved. They will retry.',
          type: 'error',
        });
      });
    }, OUTBOX_FLUSH_DELAY_MS);
  }, [flushEntries]);

  // A backgrounded tab can be discarded without warning, so treat leaving the
  // page as a deadline. `pagehide` fires on the bfcache path where `unload`
  // does not; Firestore persists the queued write locally either way.
  useEffect(() => {
    if (!uid) return;
    const flushNow = () => {
      if (outbox.size === 0) return;
      void flushEntries().catch(() => {});
    };
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') flushNow();
    };
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('pagehide', flushNow);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('pagehide', flushNow);
      flushNow();
    };
  }, [uid, outbox, flushEntries]);

  const upsertEntry = useCallback(
    async (
      cardId: string,
      patch: CollectionEntryPatch,
      options?: CollectionMutationOptions,
    ) => {
      if (!user) return undefined;

      return enqueueMutation(async () => {
        try {
          const bagRef = userCollectionBagDoc(user.uid);
          const profileRef = userDoc(user.uid);

          // When the bag listener has a trusted hydration, patch without
          // transaction gets. Field-level updates keep sibling cards when
          // another tab writes the same bag document.
          const canWriteFromLocal =
            wantEntriesRef.current && bagHydratedRef.current;

          let plan: EntryMutationPlan;
          if (canWriteFromLocal) {
            const bootstrapped = resolveMutationBaseSummary(
              summaryRef.current ?? liveSummary,
              entriesRef.current,
            );
            plan = planEntryMutation(
              entriesRef.current,
              bootstrapped,
              cardId,
              patch,
            );
            // Apply locally and hand the card to the outbox. Repeated taps on
            // the same card collapse into one write because the outbox holds
            // ids, not patches, and reads the final row at flush time.
            entriesRef.current = plan.nextEntries;
            summaryRef.current = plan.nextSummary;
            bagHydratedRef.current = true;
            setEntries(plan.nextEntries);
            outbox.add(cardId);
            if (options?.immediate || outbox.shouldFlushNow()) {
              await writePendingEntries();
            } else {
              scheduleFlush();
            }
          } else {
            // A pending patch is not on the server yet, so a transaction or a
            // cache read here would plan against a bag that is missing it.
            if (outbox.size > 0) await writePendingEntries();
            // Card FAB / cold path: only reuse IndexedDB when the bag doc
            // exists there. A cached miss is not proof of an empty bag.
            let cachedEntries: Record<string, CollectionEntry> | null = null;
            try {
              const cachedSnap = await getDocFromCache(bagRef);
              if (cachedSnap.exists()) {
                cachedEntries = parseCollectionBag(cachedSnap.data()) ?? {};
              }
            } catch {
              cachedEntries = null;
            }

            if (cachedEntries) {
              const bootstrapped = resolveMutationBaseSummary(
                summaryRef.current ?? liveSummary,
                cachedEntries,
              );
              plan = planEntryMutation(cachedEntries, bootstrapped, cardId, patch);
              const nextEntry = plan.nextEntries[cardId];
              const batch = writeBatch(db);
              applyCollectionBagEntryUpdate(
                batch,
                bagRef,
                cardId,
                nextEntry,
                plan.nextSummary.revision,
              );
              batch.set(
                profileRef,
                { collectionSummary: collectionSummaryToFirestore(plan.nextSummary) },
                COLLECTION_SUMMARY_SET_OPTIONS,
              );
              await batch.commit();
              entriesRef.current = plan.nextEntries;
              summaryRef.current = plan.nextSummary;
              bagExistsRef.current = true;
              bagHydratedRef.current = true;
              if (wantEntriesRef.current) setEntries(plan.nextEntries);
            } else {
              const bootstrapped = await ensureSummary();
              plan = await runTransaction(db, async (tx) => {
                const bagSnap = await tx.get(bagRef);
                const userSnap = await tx.get(profileRef);
                const current = parseCollectionBag(bagSnap.data()) ?? {};
                const live = parseCollectionSummary(userSnap.data()?.collectionSummary);
                const base = live
                  ?? bootstrapped
                  ?? resolveMutationBaseSummary(null, current);
                const next = planEntryMutation(current, base, cardId, patch);
                if (bagSnap.exists()) {
                  applyCollectionBagEntryUpdate(
                    tx,
                    bagRef,
                    cardId,
                    next.nextEntries[cardId],
                    next.nextSummary.revision,
                  );
                } else {
                  const bagPayload = collectionBagToFirestore(
                    next.nextEntries,
                    next.nextSummary.revision,
                  );
                  assertCollectionBagSize(bagPayload);
                  tx.set(bagRef, bagPayload);
                }
                tx.set(
                  profileRef,
                  { collectionSummary: collectionSummaryToFirestore(next.nextSummary) },
                  COLLECTION_SUMMARY_SET_OPTIONS,
                );
                return next;
              });
            }
          }

          // Orphaned binder placements are reconciled on the binder page,
          // which already holds live placements and live entries. Doing it
          // here read every binder plus a placement query each — more reads to
          // remove one card than to load the entire collection. Rules still
          // block *new* over-placements, so nothing new can go wrong meanwhile.

          if (plan.nextQuantity > plan.previousQuantity) {
            trackEvent(AnalyticsEvents.COLLECTION_ADD);
          } else if (plan.nextQuantity < plan.previousQuantity) {
            trackEvent(AnalyticsEvents.COLLECTION_REMOVE);
          }

          return {
            quantity: plan.nextQuantity,
            quantityFoil: plan.nextQuantityFoil,
            lots: plan.nextEntries[cardId]?.lots,
          };
        } catch (err) {
          throw wrapFirestoreQuotaError(err);
        }
      });
    },
    [
      user,
      liveSummary,
      ensureSummary,
      enqueueMutation,
      outbox,
      scheduleFlush,
      writePendingEntries,
    ],
  );

  const removeEntry = useCallback(
    async (cardId: string) => {
      if (!user) return;
      await upsertEntry(cardId, { quantity: 0, quantityFoil: 0 });
    },
    [user, upsertEntry],
  );

  const clearCollection = useCallback(async () => {
    if (!user) throw new Error('Must be signed in.');
    // Owner-only: always targets the signed-in user's collection path.
    // Firestore rules also require request.auth.uid == uid for writes.
    try {
      await enqueueMutation(async () => {
        // Deleting everything supersedes anything still queued; flushing it
        // first would only write rows this call is about to erase.
        cancelScheduledFlush();
        outbox.clear();
        const next = emptyCollectionSummary((liveSummary?.revision ?? 0) + 1);
        await writeCollectionBag(user.uid, {}, next.revision);
        await clearAllCustomBinderPlacements(user.uid).catch((err) => {
          console.warn('[useCollection] binder placement clear failed (non-fatal)', err);
        });
        await setDoc(
          userDoc(user.uid),
          { collectionSummary: collectionSummaryToFirestore(next) },
          COLLECTION_SUMMARY_SET_OPTIONS,
        );
        entriesRef.current = {};
        summaryRef.current = next;
        bagExistsRef.current = true;
        setEntries({});
        trackEvent(AnalyticsEvents.COLLECTION_DELETE);
      });
    } catch (err) {
      throw wrapFirestoreQuotaError(err);
    }
  }, [user, liveSummary?.revision, enqueueMutation, outbox, cancelScheduledFlush]);

  // Debounced sync of compact projection for team aggregate browser.
  useEffect(() => {
    if (!user?.teamId || loading || !wantEntries) return;
    const teamId = user.teamId;
    const visibility = user.collectionVisibility;
    const handle = window.setTimeout(() => {
      void import('../features/teams/utils/teamSharedCollectionSync')
        .then(({ syncTeamSharedCollection, visibilitySharesWithTeam }) =>
          syncTeamSharedCollection({
            teamId,
            uid: user.uid,
            displayName: user.displayName,
            slug: user.slug,
            entries,
            visibility,
            // Public/team visibility always projects; private clears the doc.
            shareCollection: visibilitySharesWithTeam(visibility),
            revision: liveSummary?.revision,
          })
        )
        .catch((err) => {
          console.warn('[useCollection] team shared sync failed (non-fatal)', err);
        });
    }, 800);
    return () => window.clearTimeout(handle);
  }, [
    user?.teamId,
    user?.uid,
    user?.displayName,
    user?.slug,
    user?.collectionVisibility,
    liveSummary?.revision,
    entries,
    loading,
    wantEntries,
  ]);

  const getEntry = useCallback((cardId: string) => entries[cardId], [entries]);

  const totals = useMemo(() => {
    let totalCards = 0;
    let totalFoilCards = 0;
    let uniqueCards = 0;
    for (const e of Object.values(entries)) {
      if (e.quantity > 0) {
        uniqueCards += 1;
        totalCards += e.quantity;
        totalFoilCards += e.quantityFoil ?? 0;
      }
    }
    return { totalCards, uniqueCards, totalFoilCards };
  }, [entries]);

  const value: ContextValue = {
    entries,
    loading,
    entriesLive: wantEntries,
    upsertEntry,
    flushEntries,
    removeEntry,
    clearCollection,
    getEntry,
    totalCards: totals.totalCards,
    uniqueCards: totals.uniqueCards,
    totalFoilCards: totals.totalFoilCards,
    retainEntries,
  };

  return (
    <CollectionContext.Provider value={value}>{children}</CollectionContext.Provider>
  );
}

export function useCollection(options?: UseCollectionOptions): ContextValue {
  const ctx = useContext(CollectionContext);
  if (!ctx) throw new Error('useCollection must be used inside CollectionProvider');
  const subscribe = options?.subscribe !== false;
  useEffect(() => {
    if (subscribe) ctx.retainEntries();
  }, [subscribe, ctx.retainEntries]);
  return ctx;
}
